import { useState } from 'react';

import { useSola } from '../../sola.jsx';
import {
  subConfirmed,
  subPending,
  subRejected,
  totalSubConfirmed,
  totalSubPending,
  totalSubRejected,
  totalSubs,
} from '../../lib/progress.js';
import { Bar, StatusBadge } from '../../components/ui/bits.jsx';
import { useUI } from '../../components/ui/feedback.jsx';
import * as api from '../../lib/api.js';
import { Icon } from '../../components/ui/Icon.jsx';
import { t } from '../../i18n/index.js';

/*
  Program šolanja: dva sklopa, v vsakem vaje s pod-vajami.

  Vaja se razpre s pravim <button aria-expanded>, ne z <div onClick> —
  tipkovnica in bralnik zaslona zdaj vesta, kaj se dogaja.
*/

export function Program({
  uid,
  progress,
  explanations,
  onOpenExplanation,
  onChanged,
  readOnly = false,
}) {
  const [open, setOpen] = useState(null);
  const { program } = useSola();

  return (
    <div className="stack stack--loose">
      {program.sklopi.map(({ key, label, list }) => (
        <Section
          key={key}
          label={label}
          list={list}
          uid={uid}
          progress={progress}
          explanations={explanations}
          open={open}
          setOpen={setOpen}
          onOpenExplanation={onOpenExplanation}
          onChanged={onChanged}
          readOnly={readOnly}
        />
      ))}
    </div>
  );
}

function Section({
  label,
  list,
  uid,
  progress,
  explanations,
  open,
  setOpen,
  onOpenExplanation,
  onChanged,
  readOnly,
}) {
  const confirmed = totalSubConfirmed(list, progress);
  const pending = totalSubPending(list, progress);
  const rejected = totalSubRejected(list, progress);
  const total = totalSubs(list);

  return (
    <section className="card">
      <div className="card__head">
        <div className="spacer">
          <h2 className="t-lg">{label}</h2>
          <p className="t-sm t-dim">
            {confirmed}/{total} {t('potrjenih')}
            {pending > 0 && <span className="t-wait"> · {pending} {t('čaka potrditev')}</span>}
            {rejected > 0 && <span className="t-bad"> · {rejected} {t('zavrnjenih')}</span>}
          </p>
        </div>
        <Bar done={confirmed} total={total} label={t('{sklop}: {a} od {b} potrjenih', { sklop: label, a: confirmed, b: total })} />
        <span className="t-md t-black t-brand num" style={{ minWidth: 44, textAlign: 'right' }}>
          {total ? Math.round((confirmed / total) * 100) : 0}%
        </span>
      </div>

      <div className="list">
        {list.map((ex) => (
          <Exercise
            key={ex.id}
            ex={ex}
            uid={uid}
            progress={progress}
            explanations={explanations}
            isOpen={open === ex.id}
            onToggle={() => setOpen(open === ex.id ? null : ex.id)}
            onOpenExplanation={onOpenExplanation}
            onChanged={onChanged}
            readOnly={readOnly}
          />
        ))}
      </div>
    </section>
  );
}

function Exercise({
  ex,
  uid,
  progress,
  explanations,
  isOpen,
  onToggle,
  onOpenExplanation,
  onChanged,
  readOnly,
}) {
  const { toast } = useUI();
  const ep = progress[ex.id] ?? {};
  const pending = subPending(ex, progress);
  const confirmed = subConfirmed(ex, progress);
  const rejected = subRejected(ex, progress);
  const all = ex.subs.length;
  const complete = confirmed === all;
  const hasExp = !!explanations[`${ex.id}:-1`];

  const write = async (fn, okMsg) => {
    try {
      await fn();
      if (okMsg) toast.ok(okMsg);
      await onChanged();
    } catch (e) {
      toast.bad(e.message ?? t('Shranjevanje ni uspelo.'));
    }
  };

  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <div className="row" style={{ padding: 'var(--s-2) var(--s-4)' }}>
        <button
          className="row spacer"
          aria-expanded={isOpen}
          aria-controls={`ex-${ex.id}`}
          onClick={onToggle}
          style={{ padding: 'var(--s-2) 0', textAlign: 'left', minHeight: 'var(--tap)' }}
        >
          <Dot complete={complete} pending={pending > 0} />
          <span className="spacer">
            <span className="t-md t-bold" style={{ display: 'block' }}>
              {ex.title}
            </span>
            <span className="t-xs t-dim num">
              {confirmed}/{all} {t('potrjenih')}
              {pending > 0 && <span className="t-wait"> · {pending} {t('čaka')}</span>}
              {rejected > 0 && <span className="t-bad"> · {rejected} {t('zavrnjenih')}</span>}
            </span>
          </span>
          <Icon name={isOpen ? 'up' : 'down'} size={18} className="t-dimmer" />
        </button>

        {hasExp && (
          <button
            className="btn btn--sm btn--secondary"
            onClick={() => onOpenExplanation(ex.id, -1, ex.title, t('Razlaga vaje'))}
          >
            <Icon name="info" size={15} /> {t('Razlaga')}
          </button>
        )}
      </div>

      {isOpen && (
        <div id={`ex-${ex.id}`} style={{ background: 'var(--surface-2)' }}>
          {ex.subs.map((sub, si) => (
            <SubRow
              key={si}
              sub={sub}
              state={ep.subs?.[si] ?? {}}
              hasExp={!!explanations[`${ex.id}:${si}`]}
              onExplain={() => onOpenExplanation(ex.id, si, sub, t('Razlaga pod-vaje'))}
              readOnly={readOnly}
              onToggle={(checked) => write(() => api.toggleSub(uid, ex.id, si, checked))}
              onDate={(d) => write(() => api.setSubDate(uid, ex.id, si, d))}
            />
          ))}

          <div style={{ padding: 'var(--s-4)', borderTop: '1px solid var(--border)' }}>
            <label className="field">
              <span className="field__label">{t('Tvoja opomba k vaji')}</span>
              <textarea
                className="input"
                rows={2}
                disabled={readOnly}
                defaultValue={ep.comment ?? ''}
                placeholder={t('Pogoji, kako je šlo, kaj želiš ponoviti …')}
                onBlur={(e) => {
                  if (e.target.value === (ep.comment ?? '')) return;
                  write(() => api.saveExComment(uid, ex.id, e.target.value), t('Opomba shranjena'));
                }}
              />
            </label>

            {ep.instructor_comment && (
              <p
                className="card card--brand card--pad row t-sm t-brand"
                style={{ marginTop: 'var(--s-3)', gap: 'var(--s-2)', alignItems: 'flex-start' }}
              >
                <Icon name="edit" size={15} /> {t('Inštruktor')}: „{ep.instructor_comment}“
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SubRow({ sub, state, hasExp, onExplain, onToggle, onDate, readOnly }) {
  const locked = state.status === 'confirmed';
  const checked = !!state.done || state.status === 'pending' || locked;
  const tone =
    state.status === 'pending' ? ' list__row--wait' : locked ? ' list__row--ok' : '';

  return (
    <div className={`subrow${tone}`}>
      <input
        type="checkbox"
        className="checkbox"
        checked={checked}
        disabled={locked || readOnly}
        aria-label={sub}
        title={locked ? t('Potrjene vaje ni mogoče spremeniti') : undefined}
        onChange={(e) => onToggle(e.target.checked)}
      />

      <span className={`subrow__title t-md${locked ? ' t-ok' : ''}`}>{sub}</span>

      {hasExp ? (
        <button className="btn-icon btn-icon--sm" onClick={onExplain} aria-label={t('Razlaga: {sub}', { sub })}>
          <Icon name="info" size={16} />
        </button>
      ) : (
        <span />
      )}

      <span className="subrow__meta">
        {/* Stari zapisi so lahko odkljukani brez statusa — takrat prav tako
            čakajo inštruktorja. */}
        <StatusBadge status={state.status ?? (state.done ? 'pending' : null)} />

        <input
          type="date"
          className={`input subrow__date${state.date ? '' : ' subrow__date--empty'}`}
          disabled={readOnly || locked}
          value={state.date ?? ''}
          aria-label={t('Datum za: {sub}', { sub })}
          title={locked ? t('Datum potrjene vaje spremeni inštruktor') : t('Datum opravljanja')}
          onChange={(e) => onDate(e.target.value)}
        />
      </span>
    </div>
  );
}

function Dot({ complete, pending }) {
  const bg = complete ? 'var(--ok-fill)' : pending ? 'var(--wait-fill)' : 'var(--surface-3)';
  return (
    <span
      aria-hidden="true"
      style={{
        width: 26,
        height: 26,
        borderRadius: '50%',
        background: bg,
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
        color: '#fff',
        fontSize: 13,
        fontWeight: 800,
      }}
    >
      {complete ? <Icon name="check" size={15} strokeWidth={2.6} /> : pending ? <Icon name="clock" size={14} /> : null}
    </span>
  );
}
